import { LegacyBalanceTransactionDrawer } from '@src/containers/Dealer/CustomerBalances/LegacyBalanceTransactionDrawer';
import {
  AccordionGroup,
  ACTION,
  AppwideDrawerContentType,
  CustomerLedgerUpdateTypeEnum,
  IconName,
  LookbackOption,
  useAppwideDrawerContext,
  type BlotterColumnState,
  type TabProps,
  type Transfer,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useRoleAuth } from '../../../hooks';
import { FilteredTransfers, type RecentTransfersParams } from './FilteredTransfers';
import type { TransfersBlotterFilter } from './useTransfersFilter';

export interface TransfersTabProps extends TabProps {
  defaultFilter?: Partial<TransfersBlotterFilter>;
  defaultColumns?: BlotterColumnState[];
}

export function FilteredTransfersWrapper({
  tabId,
  blotterID,
  initialIsOpen,
  defaultColumns,
  defaultFilter,
  onCloneTab: handleCloneTab,
}: RecentTransfersParams & { tabId: string }) {
  const { isAuthorized } = useRoleAuth();
  const canCreateBalanceTransaction = isAuthorized(ACTION.EDIT_CUSTOMER_BALANCE);

  const { openDrawer: openBalanceTransactionDrawer, closeDrawer: closeBalanceTransactionDrawer } =
    useAppwideDrawerContext();

  // reset form state when opening new
  const openNewBalanceTransactionDrawer = useCallback(
    (transfer?: Transfer) => {
      openBalanceTransactionDrawer({
        renderContent: () => (
          <LegacyBalanceTransactionDrawer
            entity={transfer}
            isOpen={true}
            onClose={closeBalanceTransactionDrawer}
            transactionType={CustomerLedgerUpdateTypeEnum.Deposit}
            suppressHeader={true}
          />
        ),
        type: AppwideDrawerContentType.CreateCustomerDeposit,
        title: 'New Customer Deposit',
      });
    },
    [closeBalanceTransactionDrawer, openBalanceTransactionDrawer]
  );

  const newTabDefaults = useMemo(() => {
    return {
      defaultFilter: {
        _start: LookbackOption.Past30Days,
      },
      defaultColumns,
    } satisfies TransfersTabProps;
  }, [defaultColumns]);

  const getCustomContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<Transfer>) => {
      if (!canCreateBalanceTransaction) {
        return [];
      }

      const transfer = params?.node?.data;
      if (transfer == null || transfer.ParentTransactionID || transfer.isTransferOutgoing) {
        return [];
      }

      return [
        {
          name: 'Create Customer Deposit',
          action: () => {
            openNewBalanceTransactionDrawer(transfer);
          },
          icon: `<i class="ag-icon ${IconName.DocumentDownload}"/>`,
        },
      ];
    },
    [canCreateBalanceTransaction, openNewBalanceTransactionDrawer]
  );

  return (
    <>
      <AccordionGroup key={tabId}>
        <FilteredTransfers
          key={tabId}
          blotterID={blotterID}
          defaultColumns={defaultColumns ?? newTabDefaults.defaultColumns}
          defaultFilter={defaultFilter ?? newTabDefaults.defaultFilter}
          onCloneTab={handleCloneTab}
          initialIsOpen={initialIsOpen}
          contextMenuOptions={{ getCustomContextMenuItems }}
        />
      </AccordionGroup>
    </>
  );
}
